import { useMemo, useCallback } from "react";
import { SocialChannel } from "@/database";
import { Option } from "../../..";
import DailymotionIcon from "../../../../assets/icons/svg/dailymotion-icon.svg?react";
import noImage from "../../../../assets/icons/svg/no-image.svg";

interface ChannelOption extends Option {
  customLabel: React.ReactNode;
}

interface UseChannelOptionsProps {
  socialChannels: {
    isLoading: boolean;
    socialChannelsList: SocialChannel[];
  };
  mostFrequentSocial?: {
    list?: Array<{ name?: string }>;
  };
}

interface UseChannelOptionsReturn {
  channelOptions: ChannelOption[];
  mfuChannelOptions: ChannelOption[];
  getChannelOptions: (limit?: number, social?: boolean) => ChannelOption[];
}

export const useChannelOptions = ({
  socialChannels,
  mostFrequentSocial,
}: UseChannelOptionsProps): UseChannelOptionsReturn => {
  const getChannelOptions = useCallback(
    (limit = 100, social = false): ChannelOption[] => {
      if (
        !socialChannels.isLoading &&
        socialChannels.socialChannelsList.length > 0
      ) {
        const mfSocial = mostFrequentSocial?.list?.map((data) => {
          return data?.name?.split("social_")[1];
        });
        const socialChannelsList = socialChannels.socialChannelsList
          .filter((channel: SocialChannel) => channel.status === 1)
          .sort((a: SocialChannel, b: SocialChannel) => {
            const aIndex = mfSocial?.indexOf(a.id) ?? -1;
            const bIndex = mfSocial?.indexOf(b.id) ?? -1;

            if (aIndex === -1 && bIndex === -1) return 0;
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
          });
        return socialChannelsList
          .slice(0, limit)
          .map((channel: SocialChannel) => ({
            label: channel.name,
            value: channel.id,
            customLabel: (
              <div
                className={`d-flex justify-content-start align-items-center ${
                  social && ""
                }`}
              >
                <div className="">
                  <div className="position-relative">
                    <img
                      src={
                        channel.platform === "facebook"
                          ? `https://graph.facebook.com/${channel.id}/picture?type=large`
                          : channel.image || noImage
                      }
                      alt="social"
                      height={social ? "35px" : "40px"}
                      width={social ? "35px" : "40px"}
                      className="rounded-pill bg-light"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = noImage;
                      }}
                    />
                    {channel.platform === "dailymotion" ? (
                      <DailymotionIcon
                        width={16}
                        height={16}
                        className="position-absolute"
                        style={{
                          bottom: 0,
                          padding: social ? "0 2px" : "",
                          right: -3,
                          backgroundColor: "#fff",
                          borderRadius: "4px",
                          color: "#000000",
                        }}
                      />
                    ) : (
                      <i
                        className={`bi bi-${channel.platform} position-absolute`}
                        style={{
                          bottom: 0,
                          padding: social ? "0 2px" : "",
                          right: -3,
                          backgroundColor: "#fff",
                          borderRadius: "4px",
                          color:
                            channel.platform === "youtube"
                              ? "red"
                              : channel.platform === "linkedin"
                              ? "#0077B5"
                              : "#3293fd",
                        }}
                      ></i>
                    )}
                  </div>
                </div>
                <i className={`bi bi-${channel.name} me-2`} />
                {channel.name}
              </div>
            ),
          }));
      }

      return [];
    },
    [socialChannels, mostFrequentSocial]
  );

  const channelOptions = useMemo(
    () => getChannelOptions(100, false),
    [getChannelOptions]
  );

  const mfuChannelOptions = useMemo(
    () => getChannelOptions(5, true),
    [getChannelOptions]
  );

  return {
    channelOptions,
    mfuChannelOptions,
    getChannelOptions,
  };
};
